import React, { useState, useEffect } from 'react';
import {
    Calendar as CalendarIcon,
    ChevronLeft,
    ChevronRight,
    Clock,
    User,
    Plus,
    Loader2,
    Activity,
    AlertCircle,
    MoreHorizontal,
    MapPin,
    DollarSign,
    Home,
    UserPlus,
    FileText,
    CheckCircle2,
    Phone,
    Mail,
    Bell,
    TrendingUp
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { Textarea } from "@/components/ui/textarea";
import { mockActivities } from "@/data/activities";
import { ActivityItem } from "./ActivityItem";
import { DashboardMetricsSkeleton } from "./DashboardMetricsSkeleton";

/* --- 1. VISIT CALENDAR WIDGET --- */
export const VisitCalendar = () => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [appointments, setAppointments] = useState([]);
    const [selectedDay, setSelectedDay] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [daysWithAppointments, setDaysWithAppointments] = useState({});

    // Modals state
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [newApt, setNewApt] = useState({
        title: '',
        date: '',
        time: '',
        contact: '',
        notes: ''
    });

    // Fetch appointments cuando cambia el mes
    useEffect(() => {
        fetchMonthAppointments(currentDate);
    }, [currentDate]);

    const fetchMonthAppointments = async (date) => {
        setLoading(true);
        setError(null);
        try {
            const year = date.getFullYear();
            const month = date.getMonth() + 1;

            const monthResponse = await fetch(`/api.php?action=get_appointments_month&month=${month}&year=${year}`, { credentials: 'include' });
            if (monthResponse.ok) {
                const monthData = await monthResponse.json();
                if (monthData.success) {
                    setDaysWithAppointments(monthData.dates || {});
                }
            }

            const firstDay = new Date(year, month - 1, 1).toISOString().split('T')[0];
            const lastDay = new Date(year, month, 0).toISOString().split('T')[0];

            const response = await fetch(`/api.php?action=get_appointments&start_date=${firstDay}&end_date=${lastDay}`, { credentials: 'include' });
            if (response.ok) {
                const data = await response.json();
                setAppointments(data.appointments || []);
            }
        } catch (err) {
            setError(err.message);
            console.error('Failed to fetch appointments:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAppointment = async () => {
        if (!newApt.title || !newApt.date) return;
        try {
            const response = await fetch('/api.php?action=create_appointment', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newApt)
            });
            const data = await response.json();
            if (data.success) {
                setIsCreateModalOpen(false);
                setNewApt({ title: '', date: '', time: '', contact: '', notes: '' });
                fetchMonthAppointments(currentDate);
            }
        } catch (err) {
            console.error("Error creating appointment:", err);
        }
    };

    const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const getAppointmentsForDay = (day) => {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return appointments.filter(apt => apt.date === dateStr);
    };

    const handleDayClick = (day) => {
        setSelectedDay(day);
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        setNewApt(prev => ({ ...prev, date: dateStr }));
    };

    const renderDays = () => {
        const totalDays = daysInMonth(year, month);
        const blankDays = firstDayOfMonth(year, month);
        const today = new Date();
        const days = [];

        for (let i = 0; i < blankDays; i++) {
            days.push(<div key={`blank-${i}`} className="h-10 w-full"></div>);
        }

        for (let d = 1; d <= totalDays; d++) {
            const dayAppointments = getAppointmentsForDay(d);
            const hasEvent = dayAppointments.length > 0;
            const isToday = d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
            const isSelected = d === selectedDay;

            days.push(
                <div
                    key={d}
                    onClick={() => handleDayClick(d)}
                    className={cn(
                        "relative group flex flex-col items-center justify-center h-10 w-full cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-all",
                        isSelected && "ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20"
                    )}
                >
                    <span className={cn(
                        "text-sm font-black",
                        isToday ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 w-7 h-7 flex items-center justify-center rounded-full" : "text-slate-600 dark:text-slate-400"
                    )}>
                        {d}
                    </span>
                    {hasEvent && <div className="absolute bottom-1 w-1 h-1 bg-blue-500 rounded-full"></div>}
                </div>
            );
        }
        return days;
    };

    const selectedDayAppointments = selectedDay ? getAppointmentsForDay(selectedDay) : appointments.slice(0, 3);

    return (
        <>
            <Card className="h-full border-0 shadow-2xl bg-white dark:bg-slate-900 overflow-hidden">
                <CardHeader className="p-6 border-b border-slate-50 dark:border-slate-800 flex flex-row shadow-sm items-center justify-between">
                    <div>
                        <CardTitle className="text-xl font-black italic flex items-center gap-2">
                            <CalendarIcon className="w-5 h-5 text-blue-500" />
                            Gestión de Citas
                        </CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCurrentDate(new Date(year, month - 1))}><ChevronLeft /></Button>
                        <span className="text-[10px] font-black uppercase tracking-widest">{monthNames[month]} {year}</span>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCurrentDate(new Date(year, month + 1))}><ChevronRight /></Button>
                    </div>
                </CardHeader>

                {/* View Selector */}
                <div className="px-6 pb-3 flex gap-1">
                    <button className="px-3 py-1.5 text-xs font-bold rounded-lg bg-blue-600 text-white shadow-sm">
                        Mes
                    </button>
                    <button className="px-3 py-1.5 text-xs font-bold rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 transition-colors">
                        Semana
                    </button>
                    <button className="px-3 py-1.5 text-xs font-bold rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 transition-colors">
                        Día
                    </button>
                    <button className="px-3 py-1.5 text-xs font-bold rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 transition-colors">
                        Agenda
                    </button>
                </div>

                <CardContent className="p-6">
                    <div className="grid grid-cols-7 gap-1 text-center mb-4">
                        {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map(d => (
                            <div key={d} className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">{d}</div>
                        ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                        {renderDays()}
                    </div>

                    <div className="mt-8 space-y-4">
                        <div className="flex items-center justify-between">
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                {selectedDay ? `Eventos del día ${selectedDay}` : 'Próximas Visitas'}
                            </h4>
                            <Button size="sm" variant="ghost" className="h-7 text-[10px] font-black uppercase text-blue-600" onClick={() => setIsCreateModalOpen(true)}>
                                <Plus className="w-3 h-3 mr-1" /> Agendar
                            </Button>
                        </div>

                        <div className="space-y-2">
                            {selectedDayAppointments.length > 0 ? (
                                selectedDayAppointments.map((apt, i) => (
                                    <div
                                        key={i}
                                        onClick={() => { setSelectedAppointment(apt); setIsDetailModalOpen(true); }}
                                        className="flex items-center gap-4 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200/50 dark:border-slate-800 hover:border-blue-500/50 dark:hover:border-blue-500/50 cursor-pointer transition-all hover:translate-x-1"
                                    >
                                        <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex flex-col items-center justify-center shrink-0 shadow-lg shadow-blue-500/10">
                                            <span className="text-[16px] font-black leading-none">{new Date(apt.date).getDate()}</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-black text-slate-900 dark:text-white truncate uppercase italic tracking-tight">{apt.title}</p>
                                            <div className="flex items-center gap-3 mt-1">
                                                <span className="text-[10px] font-bold text-slate-500 flex items-center gap-1"><Clock size={10} /> {apt.time}</span>
                                                <span className="text-[10px] font-bold text-blue-500 flex items-center gap-1 uppercase"><User size={10} /> {apt.contact || 'S/C'}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="py-6 text-center border-2 border-dashed border-slate-100 dark:border-slate-800 rounded-2xl">
                                    <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">No hay citas programadas</p>
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Modal de Creación */}
            <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-black uppercase italic tracking-tighter">Nueva Cita</DialogTitle>
                        <DialogDescription>Completa los detalles para agendar la visita.</DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="space-y-1">
                            <Label className="text-[10px] font-black uppercase ml-1">Motivo / Título</Label>
                            <Input value={newApt.title} onChange={e => setNewApt({ ...newApt, title: e.target.value })} placeholder="Ej. Visita Casa Santa Elena" className="rounded-xl" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <Label className="text-[10px] font-black uppercase ml-1">Fecha</Label>
                                <Input type="date" value={newApt.date} onChange={e => setNewApt({ ...newApt, date: e.target.value })} className="rounded-xl" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-[10px] font-black uppercase ml-1">Hora</Label>
                                <Input type="time" value={newApt.time} onChange={e => setNewApt({ ...newApt, time: e.target.value })} className="rounded-xl" />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[10px] font-black uppercase ml-1">Contacto / Cliente</Label>
                            <Input value={newApt.contact} onChange={e => setNewApt({ ...newApt, contact: e.target.value })} placeholder="Nombre completo" className="rounded-xl" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[10px] font-black uppercase ml-1">Notas</Label>
                            <Textarea value={newApt.notes} onChange={e => setNewApt({ ...newApt, notes: e.target.value })} placeholder="Detalles de la visita..." className="rounded-xl min-h-[80px]" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleCreateAppointment} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black rounded-xl h-12 shadow-xl shadow-blue-600/20">Confirmar Cita</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal de Detalle */}
            <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-black uppercase italic tracking-tighter">Detalle de la Cita</DialogTitle>
                    </DialogHeader>
                    {selectedAppointment && (
                        <div className="space-y-6 py-4">
                            <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800">
                                <h3 className="text-lg font-black text-slate-900 dark:text-white mb-2">{selectedAppointment.title}</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
                                        <CalendarIcon size={14} className="text-blue-500" /> {selectedAppointment.date}
                                    </div>
                                    <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
                                        <Clock size={14} className="text-blue-500" /> {selectedAppointment.time}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <p className="text-[10px] font-black uppercase text-slate-400 mb-1 ml-1">Cliente / Contacto</p>
                                    <div className="flex items-center gap-3 p-3 bg-white dark:bg-slate-950 border border-slate-100 dark:border-slate-800 rounded-xl">
                                        <div className="h-8 w-8 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-full flex items-center justify-center">
                                            <User size={16} />
                                        </div>
                                        <span className="font-bold text-sm">{selectedAppointment.contact || 'No especificado'}</span>
                                    </div>
                                </div>

                                {selectedAppointment.notes && (
                                    <div>
                                        <p className="text-[10px] font-black uppercase text-slate-400 mb-1 ml-1">Notas de la Cita</p>
                                        <div className="p-4 bg-white dark:bg-slate-950 border border-slate-100 dark:border-slate-800 rounded-xl text-xs font-medium leading-relaxed">
                                            {selectedAppointment.notes}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDetailModalOpen(false)} className="w-full rounded-xl font-black uppercase text-[10px]">Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

/* --- 2. DETAILED TIMELINE WIDGET --- */
export const DetailedTimeline = () => {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchActivities();
    }, []);

    const fetchActivities = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/api.php?action=activity_feed&limit=10', {
                credentials: 'include'
            });
            if (!response.ok) throw new Error('Error fetching activities');

            const data = await response.json();
            setActivities(data.activities || []);
        } catch (err) {
            setError(err.message);
            console.error('Failed to fetch activities:', err);
        } finally {
            setLoading(false);
        }
    };

    const getColorClass = (type) => {
        const colors = {
            'lead': 'bg-blue-500',
            'call': 'bg-amber-500',
            'doc': 'bg-green-500',
            'event': 'bg-purple-500',
            'publish': 'bg-orange-500',
        };
        return colors[type] || 'bg-slate-400';
    };

    return (
        <Card className="h-full shadow-sm border-0 bg-white dark:bg-slate-900 shadow-xl shadow-blue-900/5 flex flex-col">
            <CardHeader className="pb-2 border-b border-slate-50 dark:border-slate-800">
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900 dark:text-white">
                    <Activity className="w-5 h-5 text-orange-500" />
                    Historial de Actividad
                </CardTitle>
                <CardDescription className="text-xs font-medium">Registro profesional de acciones en tiempo real.</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 p-4 overflow-y-auto max-h-[500px] flex-1">
                {error && (
                    <div className="mb-4 p-2 bg-red-50 dark:bg-red-900/20 rounded text-red-600 dark:text-red-300 text-xs flex items-center gap-2">
                        <AlertCircle className="w-3 h-3" />
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="flex gap-4 animate-pulse">
                                <div className="w-8 h-8 rounded-full bg-slate-100" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 bg-slate-100 rounded w-3/4" />
                                    <div className="h-2 bg-slate-100 rounded w-1/4" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : activities.length > 0 ? (
                    <div className="space-y-1">
                        {activities.map((item, i) => (
                            <ActivityItem
                                key={i}
                                activity={item}
                                isLast={i === activities.length - 1}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="space-y-1">
                        {mockActivities.map((activity, i) => (
                            <ActivityItem
                                key={activity.id}
                                activity={activity}
                                isLast={i === mockActivities.length - 1}
                            />
                        ))}
                    </div>
                )}
            </CardContent>
            <div className="p-3 bg-slate-50 border-t text-center">
                <Button variant="ghost" size="sm" className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    Ver registro completo
                </Button>
            </div>
        </Card>
    );
};

/* --- 3. PRO DISTRIBUTION CHART (Price/Zone) --- */
export const ProDistributionChart = () => {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchSummary();
    }, []);

    const fetchSummary = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/api.php?action=dashboard_summary', {
                credentials: 'include'
            });
            if (!response.ok) throw new Error('Error fetching summary');

            const data = await response.json();
            setSummary(data || {});
        } catch (err) {
            setError(err.message);
            console.error('Failed to fetch summary:', err);
        } finally {
            setLoading(false);
        }
    };

    // Datos por defecto mientras carga o si hay error
    const zoneData = [
        { name: "San Benito", count: summary?.active_properties || 0, color: "bg-blue-600", percent: 45 },
        { name: "Escalón", count: 5, color: "bg-orange-500", percent: 28 },
        { name: "Santa Elena", count: 3, color: "bg-green-500", percent: 17 },
        { name: "Otros", count: 2, color: "bg-slate-400", percent: 10 },
    ];

    const priceRanges = [
        { label: "< $100k", count: 4, color: "emerald" },
        { label: "$100k - $250k", count: 9, color: "blue" },
        { label: "$250k - $500k", count: 3, color: "orange" },
        { label: "> $500k", count: 2, color: "purple" },
    ];

    return (
        <Card className="h-full shadow-sm border-0 bg-white dark:bg-slate-900 shadow-xl shadow-blue-900/5">
            <CardHeader className="pb-0 border-b-0">
                <CardTitle className="text-lg flex items-center justify-between text-slate-900 dark:text-white">
                    Distribución Pro
                    <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button>
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 p-4 space-y-6">
                {error && (
                    <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded text-red-600 dark:text-red-300 text-xs flex items-center gap-2">
                        <AlertCircle className="w-3 h-3" />
                        {error}
                    </div>
                )}

                {loading && (
                    <div className="flex items-center justify-center py-8 text-slate-400">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Cargando...
                    </div>
                )}

                {!loading && summary && (
                    <>
                        {/* Zone Breakdown */}
                        <div className="space-y-3">
                            <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                <span>Propiedades Publicadas</span>
                                <MapPin className="w-3 h-3" />
                            </div>
                            <div className="space-y-3">
                                <div className="space-y-1">
                                    <div className="flex justify-between items-center text-[11px] font-medium">
                                        <span className="text-slate-700 dark:text-slate-300">Total</span>
                                        <span className="text-slate-400">{summary.active_properties || 0} props</span>
                                    </div>
                                    <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                        <div className="h-full rounded-full bg-blue-600" style={{ width: '100%' }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Inventory Value */}
                        <div className="space-y-3 pt-4 border-t border-slate-50 dark:border-slate-800">
                            <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                <span>Valor Inventario</span>
                                <DollarSign className="w-3 h-3" />
                            </div>
                            <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                                <p className="text-sm font-bold text-slate-900 dark:text-slate-100">
                                    ${summary.inventory_value ? (summary.inventory_value / 1000000).toFixed(2) : '0'}M
                                </p>
                                <p className="text-[10px] text-slate-500">Valor total en propiedades activas</p>
                            </div>
                        </div>

                        {/* Leads Summary */}
                        <div className="space-y-3 pt-4 border-t border-slate-50 dark:border-slate-800">
                            <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                <span>Leads Activos</span>
                                <User className="w-3 h-3" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                                    <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400">Total Leads</span>
                                    <p className="text-lg font-bold text-blue-600">{summary.total_leads || 0}</p>
                                </div>
                                <div className="p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                                    <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400">Tareas Pendientes</span>
                                    <p className="text-lg font-bold text-orange-600">{summary.pending_tasks || 0}</p>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
};
